import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
// import { endpoint } from '..'; // Non usato direttamente qui
import Button from '../../../components/Button/Button';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import Layout from '../../components/Layout/Layout';
import './ProjectType.scss';
import { Container, Grid, Box, Typography, Stack, Button as MuiButton, DialogContent } from '@mui/material';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import * as ProjectApiService from '../../services/ProjectApiService';
import Dialog from '../../../components/Dialog/Dialog';
import { IDynamicProjectGetData, IProject } from '../../api/IApiResponse'; // Rimosso ProjectData non usato
import * as ProjectTypeService from '../../services/ProjectTypeService';
import { EProjectType, unit as unitEnum } from '../../enum/project';
import {
    setProject,
    // Mantieni solo le azioni necessarie per dynamicProject, se ce ne sono
    // setReadOnly,
    // setHasBeenRan,
    // setDisableRun,
    initializeNewProject,
} from '../../Dynamic/store/project/projectslice';
import righelloIcon from '../../images/righello.png';
import { logDebug, logError } from '../../utils/utils';

// Definisci tipi come EProjectType per garantire type safety
const TYPE_PAVEMENTS = 'pavements';
const TYPE_WORKING_PLATFORMS = 'working_platforms'; // Categoria
const TYPE_RAILWAY = 'railways';
const TYPE_AIRPORT = 'airport';
const TYPE_CONTAINER_YARD = 'container_yard'; // Sottotipo
const TYPE_PARKING_DECK = 'parking_deck'; // Sottotipo

const ProjectType = () => {
    const { t } = useTranslation();
    const { id: projectIdFromUrl } = useParams<{ id: string }>();
    const dynamicProject = useAppSelector(state => state.Dynamic.project);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    //const [searchParams] = useSearchParams();

    const [processing, setProcessing] = useState(false); // Mantenuto per possibile uso futuro
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [isExistingProjectFlow, setIsExistingProjectFlow] = useState<boolean>(false);
    // Rimosso: const [selectedProjectType, setSelectedProjectType] = useState<string>(EProjectType.container_yard);
    const [ErrorDialog, setErrorDialog] = useState<string>('');
    const [isUnitPopupOpen, setIsUnitPopupOpen] = useState<boolean>(false);
    const [isGeogridUnitPopupOpen, setIsGeogridUnitPopupOpen] = useState<boolean>(false); // Specifico per Geogrid
    const [projectTypeForUnitSelection, setProjectTypeForUnitSelection] = useState<EProjectType | null>(null);

    // Stato per UI nuovo progetto
    const [isWorkingPlatformsExpanded, setIsWorkingPlatformsExpanded] = useState<boolean>(false);
    const [isPavementsExpanded, setIsPavementsExpanded] = useState<boolean>(false); // Stato espansione Pavements
    const [activeTypeKey, setActiveTypeKey] = useState<string | null>(null); // Per highlight selezione

    const closeErrorDialog = () => setErrorDialog('');

    /**
     * Validates if a string is a valid GUID format
     * @param guid - String to validate
     * @returns true if valid GUID format, false otherwise
     */
    const isValidGuid = (guid: string): boolean => {
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return guidRegex.test(guid);
    };

    const fetchProjectFromApiIfNeeded = async (id: string) => {
        setLoadingProject(true);
        try {
            // Use new API: GET /api/Project/projects/{guid}
            const loadResult = await ProjectApiService.loadProjectByGuid(id);

            if (!loadResult.success || !loadResult.data) {
                throw new Error(`Project ${id} not found via API: ${loadResult.error}`);
            }

            logDebug('ProjectType', 'Project loaded successfully from backend', loadResult.data);

            // Transform backend project to frontend format
            const transformedProjectData = ProjectApiService.transformProjectFromBackend(loadResult.data);

            logDebug('ProjectType', 'Transformed project data:', transformedProjectData);

            dispatch(setProject(transformedProjectData));
            // setIsExistingProjectFlow verrà aggiornato dall'altro useEffect
            // che dipende da dynamicProject.data
        } catch (error) {
            logError('ProjectType', 'Error fetching project from API:', error);
            setErrorDialog(t('errors.projectLoad.message') || 'Error loading project.');
            // Potresti voler navigare via o mostrare uno stato di errore più permanente
        } finally {
            setLoadingProject(false);
        }
    };

    useEffect(() => {
        const projectFromState = dynamicProject.data;
        logDebug('ProjectType', 'Project ID from URL:', projectIdFromUrl);
        logDebug('ProjectType', 'Current Redux project data:', JSON.stringify(projectFromState));

        if (!projectIdFromUrl) {
            logError('ProjectType', 'No Project ID in URL. This page requires a project ID.');
            setErrorDialog('Navigation error: Project ID is missing in URL.');
            setLoadingProject(false);
            return;
        }

        // Validate GUID format
        if (!isValidGuid(projectIdFromUrl)) {
            logError('ProjectType', 'Invalid Project ID format in URL:', projectIdFromUrl);
            setErrorDialog('Navigation error: Invalid Project ID format.');
            setLoadingProject(false);
            return;
        }

        setLoadingProject(true);
        if (projectFromState?.guid === projectIdFromUrl) {
            logDebug('ProjectType', 'Redux data matches URL ID:', projectIdFromUrl);
            // I dati in Redux sono per il progetto corretto. Determina il flusso.
            // Un progetto è "esistente" (tipo non modificabile) se newProject è false.
            const isActuallyExisting = !projectFromState.newProject;
            setIsExistingProjectFlow(isActuallyExisting);

            if (isActuallyExisting) {
                // Se il progetto esiste già (tipo definito o geogrid inizializzato),
                // non mostrare le opzioni di selezione tipo/sottotipo.
                setIsWorkingPlatformsExpanded(false);
                setActiveTypeKey(null);
            }
            setLoadingProject(false);
        } else {
            // I dati in Redux non corrispondono o mancano, carica dall'API
            logDebug('ProjectType', 'Redux data mismatch or missing for URL ID. Fetching project:', projectIdFromUrl);
            // fetchProjectFromApiIfNeeded gestirà setLoadingProject(false) e aggiornerà Redux,
            // il che triggererà un re-run di questo useEffect.
            fetchProjectFromApiIfNeeded(projectIdFromUrl);
        }
        // Rimuovi fetchProjectFromApiIfNeeded dalle dipendenze se causa loop,
        // la logica di fetch è condizionata da projectIdFromUrl e dynamicProject.data.guid
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectIdFromUrl, dynamicProject.data, dispatch, t]);

    const proceedToDesign = (projectType: EProjectType, unitChoice: 'metric' | 'imperial') => {
        if (!projectIdFromUrl) {
            logError('ProjectType', 'Project ID from URL is missing in proceedToDesign.');
            setErrorDialog('Cannot proceed: Project ID is missing.');
            return;
        }

        if (!isValidGuid(projectIdFromUrl)) {
            logError('ProjectType', 'Invalid Project ID format in proceedToDesign:', projectIdFromUrl);
            setErrorDialog('Cannot proceed: Invalid Project ID format.');
            return;
        }

        setProcessing(true);
        const unitToUse = unitChoice === 'imperial' ? unitEnum.Imperial : unitEnum.SI;
        const method = ProjectTypeService.defaultProjectMethod[projectType]?.[0] || '';

        dispatch(
            initializeNewProject({
                guid: projectIdFromUrl, // Preserva GUID dall'URL
                type: projectType,
                unit: unitToUse,
                method: method,
                newProject: false, // Mantiene dati esistenti ma aggiorna type/unit
            })
        );

        // Navigate to appropriate route based on project type
        if (projectType === EProjectType.geogrid) {
            navigate(`/geogrid/${projectIdFromUrl}`);
        } else {
            navigate(`/designCondition/${projectIdFromUrl}`);
        }
    };

    const handleUnitSelected = (unitValue: 'metric' | 'imperial') => {
        setIsUnitPopupOpen(false); // Chiude il popup per progetti standard

        if (projectTypeForUnitSelection) {
            // Passa 'unitValue' direttamente a proceedToDesign
            proceedToDesign(projectTypeForUnitSelection, unitValue);
        } else {
            logError('ProjectType', 'Project type not available during unit selection for standard project.');
        }
        setProjectTypeForUnitSelection(null); // Resetta dopo l'uso
    };

    // Handler per il click su "Working Platforms" (solo toggle)
    // !!! TODO: al momento gestisce active come classe e lo elimina al secondo clic, fare anceh per altri o gestire
    // active separatamente
    const handleWorkingPlatformsClick = () => {
        if (!isExistingProjectFlow) {
            const isAlreadyActive = activeTypeKey === TYPE_WORKING_PLATFORMS;
            setIsWorkingPlatformsExpanded(!isAlreadyActive);
            setActiveTypeKey(isAlreadyActive ? null : TYPE_WORKING_PLATFORMS);
        }
    };

    const handlePavementsClick = () => {
        if (!isExistingProjectFlow) {
            const isAlreadyActive = activeTypeKey === TYPE_PAVEMENTS;
            setIsPavementsExpanded(!isAlreadyActive);
            setActiveTypeKey(isAlreadyActive ? null : TYPE_PAVEMENTS);
        }
    };

    const handleFinalTypeClick = (type: string) => {
        if (!isExistingProjectFlow) {
            setActiveTypeKey(type); // Mantieni l'highlight
            setProjectTypeForUnitSelection(type as EProjectType); // Memorizza il tipo scelto
            setIsUnitPopupOpen(true); // Apri il popup delle unità!
        }
    };

    /**
     * Gestisce la selezione dell'unità specificamente per il flusso Geogrid Studio.
     * @param unitValue L'unità selezionata ('metric' o 'imperial').
     */
    const handleGeogridUnitSelected = async (unitValue: 'metric' | 'imperial') => {
        if (!projectIdFromUrl) {
            logError('ProjectType', 'Project ID from URL is missing in handleGeogridUnitSelected.');
            setErrorDialog('Cannot proceed: Project ID is missing.');
            return;
        }

        if (!isValidGuid(projectIdFromUrl)) {
            logError('ProjectType', 'Invalid Project ID format in handleGeogridUnitSelected:', projectIdFromUrl);
            setErrorDialog('Cannot proceed: Invalid Project ID format.');
            return;
        }
        setIsGeogridUnitPopupOpen(false);
        const unitToDispatch = unitValue === 'imperial' ? unitEnum.Imperial : unitEnum.SI;

        await dispatch(
            initializeNewProject({
                guid: projectIdFromUrl, // Preserva GUID dall'URL
                type: EProjectType.geogrid,
                unit: unitToDispatch,
                method: '',
                newProject: false, // Mantiene dati esistenti ma aggiorna type/unit
            })
        );
        navigate(`/geogrid/${projectIdFromUrl}`);
    };

    const handleGeogridStudioClick = () => {
        if (!isExistingProjectFlow) {
            setIsGeogridUnitPopupOpen(true); // Apri il popup specifico per le unità Geogrid
        }
    };

    const UnitSelectionDialogContent = ({ onUnitSelected }: { onUnitSelected: (unit: 'metric' | 'imperial') => void }) => (
        <DialogContent sx={{ textAlign: 'center', minWidth: '350px' }}>
            <Box
                component="img"
                src={righelloIcon}
                alt={t('unit_selection_icon_alt', 'Icona righello') ?? ''}
                sx={{
                    width: 60,
                    height: 'auto',
                    mb: 2,
                }}
            />
            <Typography variant="body1" sx={{ mb: 3 }}>
                {t('select_unit_prompt', "Seleziona l'unità di misura da usare per questo progetto.")}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
                <MuiButton variant="contained" color="primary" onClick={() => onUnitSelected('metric')}>
                    {t('international', 'Internazional')}
                </MuiButton>
                <MuiButton variant="contained" color="secondary" onClick={() => onUnitSelected('imperial')} sx={{}}>
                    {t('imperial', 'Imperiale')}
                </MuiButton>
            </Stack>
        </DialogContent>
    );

    return (
        <Layout>
            <Container>
                <Dialog
                    open={!!ErrorDialog && ErrorDialog.length > 0}
                    title={t('error') || 'Error'}
                    onClose={closeErrorDialog}
                    actions={[{ text: t('ok') || 'OK', callback: closeErrorDialog, color: 'primary' }]}>
                    <div>{ErrorDialog}</div>
                </Dialog>
                <Dialog
                    open={isUnitPopupOpen} // Popup per progetti standard
                    title={t('select_unit_title', "Scegli l'unità di misura del progetto") ?? ''}
                    onClose={() => setIsUnitPopupOpen(false)}
                    hideCloseButton={true}
                    actions={[]}>
                    <UnitSelectionDialogContent onUnitSelected={handleUnitSelected} />
                </Dialog>

                <Dialog
                    open={isGeogridUnitPopupOpen} // Popup specifico per Geogrid
                    title={t('select_unit_title', "Scegli l'unità di misura del progetto") ?? ''}
                    onClose={() => setIsGeogridUnitPopupOpen(false)}
                    hideCloseButton={true}
                    actions={[]}>
                    <UnitSelectionDialogContent onUnitSelected={handleGeogridUnitSelected} />
                </Dialog>
                {loadingProject ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                        <Typography>Loading Project...</Typography> {/* Indicatore di caricamento */}
                    </Box>
                ) : (
                    <div className="d-flex justify-content-between w-100 full-height-wrapper projectType-container">
                        <Grid container style={{ position: 'relative', paddingTop: '0', paddingBottom: '80px', alignItems: 'flex-start' }} spacing={3}>
                            {/* --- Bottone Geogrid Studio (solo per nuovi progetti, in alto a destra) --- */}
                            {!isExistingProjectFlow && (
                                <Grid className="contBTN_geogridStudio" item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button onClick={handleGeogridStudioClick} classNameBtn="button-GeogridStudio">
                                        {t('geogrid_studio', 'Geogrid Studio')}
                                        <i className="fa-solid fa-arrow-up-right-from-square" />
                                    </Button>
                                </Grid>
                            )}
                            {/* --- Sezione Comune: Titolo e Unità --- */}
                            <Grid item xs={12} style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <Typography variant="h5" component="h1">
                                    {isExistingProjectFlow ? t('project_details') : t('select_project_type')}
                                </Typography>
                            </Grid>
                            {/* --- Sezione Specifica: Progetto Esistente o Nuovo --- */}
                            {isExistingProjectFlow ? (
                                /* --- UI per Progetto Esistente --- */
                                <Grid container item spacing={2} style={{ justifyContent: 'center', marginTop: '20px' }}>
                                    <Grid item xs={12}>
                                        <Typography className="error-color text-center" style={{ border: 'none', marginBottom: '10px' }}>
                                            {t('project_type_is', { projectType: dynamicProject.data?.type || 'N/A' })}
                                        </Typography>
                                        <Typography className="error-color text-center" style={{ border: 'none', marginBottom: '20px' }}>
                                            {t('project_type_unchangeable')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                                        <Button
                                            disabled={processing}
                                            onClick={() => {
                                                if (dynamicProject.data?.type === EProjectType.geogrid) {
                                                    navigate(`/geogrid/${dynamicProject.data?.guid}`);
                                                } else {
                                                    navigate(`/designCondition/${dynamicProject.data?.guid}`);
                                                }
                                            }}>
                                            {dynamicProject.data?.type === EProjectType.geogrid ? t('go_to_geogrid_studio', 'Go to Geogrid Studio') : t('project_info_go_to_design_condition')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            ) : (
                                /* --- UI per Nuovo Progetto (Blocchi) --- */
                                <Grid container item spacing={3} style={{ justifyContent: 'center', marginTop: '30px' }}>
                                    {/* Main Row: All 4 project types in one row */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Button
                                            classNameBtn={`project-type-main-box ${activeTypeKey === TYPE_PAVEMENTS ? 'btn-projectType-active' : 'btn-projectType'}`}
                                            onClick={handlePavementsClick}>
                                            <img src="/images/1_Macchina.png" alt="Macchina" style={{ maxWidth: 120, height: 'auto' }} />
                                            <Typography variant="h6" component="h3">
                                                {t('roads', 'Roads')}
                                            </Typography>
                                        </Button>
                                        {isPavementsExpanded && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: '24px',
                                                    marginTop: '18px',
                                                    width: '100%',
                                                }}>
                                                <Box sx={{ width: '48%' }}>
                                                    <Button
                                                        classNameBtn={`project-type-sub-box ${activeTypeKey === EProjectType.paved ? 'btn-projectType-active' : 'btn-projectType'}`}
                                                        onClick={() => handleFinalTypeClick(EProjectType.paved)}>
                                                        <Typography variant="subtitle1" component="h4">
                                                            {t('paved', 'Paved')}
                                                        </Typography>
                                                    </Button>
                                                </Box>
                                                <Box sx={{ width: '48%' }}>
                                                    <Button
                                                        classNameBtn={`project-type-sub-box ${activeTypeKey === EProjectType.unpaved ? 'btn-projectType-active' : 'btn-projectType'}`}
                                                        onClick={() => handleFinalTypeClick(EProjectType.unpaved)}>
                                                        <Typography variant="subtitle1" component="h4">
                                                            {t('unpaved', 'Unpaved')}
                                                        </Typography>
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Button
                                            classNameBtn={`project-type-main-box ${activeTypeKey === TYPE_WORKING_PLATFORMS ? 'btn-projectType-active' : 'btn-projectType'}`}
                                            onClick={handleWorkingPlatformsClick}>
                                            <img src="/images/2_Container.png" alt="Macchina" style={{ maxWidth: 120, height: 'auto' }} />
                                            <Typography variant="h6" component="h3">
                                                {t('working_platforms', 'Working Platforms')}
                                            </Typography>
                                        </Button>
                                        {/* Sub-boxes: render as a new row below the main button */}
                                        {isWorkingPlatformsExpanded && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: '24px',
                                                    marginTop: '18px',
                                                    width: '100%',
                                                }}>
                                                <Box sx={{ width: '48%' }}>
                                                    <Button
                                                        classNameBtn={`project-type-sub-box ${activeTypeKey === TYPE_CONTAINER_YARD ? 'btn-projectType-active' : 'btn-projectType'}`}
                                                        onClick={() => handleFinalTypeClick(TYPE_CONTAINER_YARD)}>
                                                        {/*}
                                                        <img src="/images/2_Container.png" alt="Macchina" style={{ maxWidth: 120, height: 'auto', marginBottom: 1 }} />
                                                        {*/}
                                                        <Typography variant="subtitle1" component="h4">
                                                            {t('container_yard', 'Container Yard')}
                                                        </Typography>
                                                    </Button>
                                                </Box>
                                                <Box sx={{ width: '48%' }}>
                                                    <Button
                                                        classNameBtn={`project-type-sub-box ${activeTypeKey === TYPE_PARKING_DECK ? 'btn-projectType-active' : 'btn-projectType'}`}
                                                        onClick={() => handleFinalTypeClick(TYPE_PARKING_DECK)}>
                                                        <Typography variant="subtitle1" component="h4">
                                                            {t('parking_deck', 'Parking Decks')}
                                                        </Typography>
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Button
                                            classNameBtn={`project-type-main-box ${activeTypeKey === TYPE_RAILWAY ? 'btn-projectType-active' : 'btn-projectType'}`}
                                            onClick={() => handleFinalTypeClick(TYPE_RAILWAY)}>
                                            <img src="/images/3_Treno.png" alt="Macchina" style={{ maxWidth: 120, height: 'auto'}} />
                                            <Typography variant="h6" component="h3">
                                                {t('railways', 'Railways')}
                                            </Typography>
                                            {/* <p>Description...</p> */}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Button
                                            classNameBtn={`project-type-main-box ${activeTypeKey === TYPE_AIRPORT ? 'btn-projectType-active' : 'btn-projectType'}`}
                                            onClick={() => handleFinalTypeClick(TYPE_AIRPORT)}>
                                            <img src="/images/4_Aereo.png" alt="Macchina" style={{ maxWidth: 120, height: 'auto' }} />
                                            <Typography variant="h6" component="h3">
                                                {t('airports', 'Airports')}
                                            </Typography>
                                            {/* <p>Description...</p> */}
                                        </Button>
                                    </Grid>
                                    {/* Rimosso bottone Next */}
                                </Grid>
                            )}
                        </Grid>
                    </div>
                )}
            </Container>
        </Layout>
    );
};

export default ProjectType;
